/**
 * /coach/athletes/:athleteId/run/:workoutId — coach-driven tablet runner.
 *
 * Mirrors the athlete WorkoutRunner but writes against the athlete (not the
 * coach). Sibling isolation is enforced server-side by athlete_id. Maxes are
 * entered inline inside FocusModeOverlay (via InlineMaxPrompt) the moment
 * each exercise comes up — no pre-workout setup page.
 *
 * Designed for a tablet handed to the kid: large tap targets, "Training on
 * behalf of NAME" sticky banner, and an exit-back to the dossier.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
    Loader2,
    ArrowLeft,
    Layers,
    Clock,
    Play,
    MessageSquare,
    UserCircle2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { CATEGORY_LABELS } from "@/lib/workoutTaxonomy";
import FocusModeOverlay from "@/components/runner/FocusModeOverlay";
import BodyweightCard from "@/components/biometrics/BodyweightCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CoachWorkoutRunner() {
    const { user, loading } = useAuth();
    const { athleteId, workoutId } = useParams();
    const navigate = useNavigate();

    const [bundle, setBundle] = useState(null);
    const [loadingData, setLoadingData] = useState(true);

    const [focusOpen, setFocusOpen] = useState(false);
    const [phase, setPhase] = useState("running");
    const [blockIdx, setBlockIdx] = useState(0);
    const [setIdx, setSetIdx] = useState(0);
    const [selectedLevels] = useState({});
    const [actuals, setActuals] = useState({});
    const [pendingDelta, setPendingDelta] = useState(0);
    const [setsCompleted, setSetsCompleted] = useState(0);
    const [startedAt, setStartedAt] = useState(null);
    const [sessionLogged, setSessionLogged] = useState(false);
    const [maxes, setMaxes] = useState({});

    useEffect(() => {
        if (!loading && (!user || !["owner", "coach"].includes(user.role))) {
            navigate("/", { replace: true });
        }
    }, [loading, user, navigate]);

    const load = useCallback(async () => {
        setLoadingData(true);
        try {
            const r = await axios.get(
                `${API}/coach/athletes/${athleteId}/runtime/${workoutId}`,
                { withCredentials: true },
            );
            setBundle(r.data);
            setMaxes(r.data.maxes || {});
        } catch (e) {
            toast.error("Couldn't load workout", { description: e?.response?.data?.detail });
            navigate(`/coach/athletes/${athleteId}`, { replace: true });
        } finally {
            setLoadingData(false);
        }
    }, [athleteId, workoutId, navigate]);

    useEffect(() => { load(); }, [load]);

    const workout = bundle?.workout;
    const exercises = bundle?.exercises || [];
    const equipment = bundle?.equipment || [];
    const athlete = bundle?.athlete;

    const equipmentMap = useMemo(
        () => Object.fromEntries(equipment.map((eq) => [eq.equipment_id, eq])),
        [equipment],
    );
    const exerciseMap = useMemo(
        () => Object.fromEntries(exercises.map((ex) => [ex.exercise_id, ex])),
        [exercises],
    );

    const requiredDrivers = useMemo(() => {
        if (!workout) return [];
        const seen = new Map();
        for (const b of workout.blocks) {
            const lvl = b.levels[selectedLevels[b.id] || 0] || b.levels[0];
            if (!lvl) continue;
            const driverId = lvl.based_off_exercise_id || lvl.exercise_id;
            const driverName = lvl.based_off_exercise_id
                ? exerciseMap[lvl.based_off_exercise_id]?.exercise_name || lvl.based_off_exercise_id
                : lvl.exercise_name;
            seen.set(driverId, { exercise_id: driverId, exercise_name: driverName });
        }
        return Array.from(seen.values());
    }, [workout, exerciseMap, selectedLevels]);

    const missingMaxes = requiredDrivers.filter((ex) => !(maxes[ex.exercise_id]?.value > 0));

    // Inline single-max save during the workout. Backend upserts by
    // (athlete_id, exercise_id) so the same exercise shared across other
    // workouts immediately reflects this value.
    const saveMaxInline = async (maxesPayload) => {
        const r = await axios.put(
            `${API}/coach/athletes/${athleteId}/maxes-bulk`,
            { maxes: maxesPayload },
            { withCredentials: true },
        );
        setMaxes(r.data || {});
        return r.data || {};
    };

    const startFocus = () => {
        setBlockIdx(0);
        setSetIdx(0);
        setPhase("running");
        setSetsCompleted(0);
        setStartedAt(new Date().toISOString());
        setSessionLogged(false);
        setFocusOpen(true);
    };

    const advanceAfterSet = () => {
        const block = workout.blocks[blockIdx];
        const level = block.levels[selectedLevels[blockIdx] || 0];
        const lastSet = setIdx + 1 >= level.sets_data.length;
        if (!lastSet) {
            setSetIdx((i) => i + 1);
            return;
        }
        if (workout.category === "strength") {
            setPendingDelta(0);
            setPhase("wheel");
        } else {
            advanceBlock();
        }
    };

    const advanceBlock = () => {
        if (blockIdx + 1 >= workout.blocks.length) {
            setPhase("done");
            return;
        }
        setBlockIdx((i) => i + 1);
        setSetIdx(0);
        setPhase("running");
    };

    const handleSetComplete = (actual) => {
        const key = `${blockIdx}_${setIdx}`;
        setActuals((a) => ({ ...a, [key]: actual }));
        setSetsCompleted((c) => c + 1);
        advanceAfterSet();
    };

    const handleConfirmWheel = async () => {
        const block = workout.blocks[blockIdx];
        const level = block.levels[selectedLevels[blockIdx] || 0];
        try {
            const r = await axios.post(
                `${API}/coach/athletes/${athleteId}/weight-intent`,
                {
                    workout_id: workoutId,
                    exercise_id: level.exercise_id,
                    delta: pendingDelta,
                    based_off_exercise_id: level.based_off_exercise_id || null,
                },
                { withCredentials: true },
            );
            if (r.data.applied && r.data.new_max != null) {
                setMaxes((m) => ({
                    ...m,
                    [level.exercise_id]: { exercise_id: level.exercise_id, value: r.data.new_max, source: "auto_progression" },
                }));
                if (pendingDelta !== 0) {
                    toast.success("1RM updated", { description: `${level.exercise_name} → ${r.data.new_max} lb` });
                }
            }
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail });
        }
        advanceBlock();
    };

    useEffect(() => {
        if (phase !== "done" || sessionLogged || !startedAt) return;
        (async () => {
            try {
                await axios.post(
                    `${API}/coach/athletes/${athleteId}/workout-sessions`,
                    {
                        workout_id: workoutId,
                        started_at: startedAt,
                        completed_at: new Date().toISOString(),
                        sets_completed: setsCompleted,
                    },
                    { withCredentials: true },
                );
                setSessionLogged(true);
                toast.success("Session logged");
            } catch (_e) { /* non-fatal */ }
        })();
    }, [phase, sessionLogged, startedAt, setsCompleted, workoutId, athleteId]);

    if (loading || !user || loadingData || !workout) {
        return (
            <div className="min-h-screen bg-brand-obsidian flex items-center justify-center">
                <Loader2 className="w-7 h-7 text-brand-red animate-spin" />
            </div>
        );
    }

    const elapsedSec = startedAt ? (new Date() - new Date(startedAt)) / 1000 : 0;
    const athleteName = athlete ? `${athlete.first_name || ""} ${athlete.last_name || ""}`.trim() : "Athlete";

    return (
        <div data-testid="coach-workout-runner" className="bg-brand-obsidian min-h-screen pb-20">
            {/* Coach context banner */}
            <div className="sticky top-0 z-30 bg-brand-red text-white px-5 py-2 flex items-center justify-between gap-3 shadow-md">
                <div className="flex items-center gap-2 min-w-0">
                    <UserCircle2 className="w-5 h-5 flex-shrink-0" />
                    <div className="min-w-0">
                        <div className="text-[10px] tracking-[0.3em] uppercase opacity-80">Tablet mode · Coach driving</div>
                        <div data-testid="coach-runner-athlete-name" className="font-display text-lg tracking-wider truncate leading-tight">
                            {athleteName}
                        </div>
                    </div>
                </div>
                <Link
                    to={`/coach/athletes/${athleteId}`}
                    data-testid="coach-runner-exit"
                    className="text-[10px] tracking-[0.25em] uppercase border border-white/40 px-3 py-1.5 hover:bg-white hover:text-brand-red transition-colors inline-flex items-center gap-1.5 flex-shrink-0"
                >
                    <ArrowLeft className="w-3 h-3" /> Exit
                </Link>
            </div>

            <section className="pt-10 pb-6 border-b border-zinc-900">
                <div className="max-w-4xl mx-auto px-6 md:px-10">
                    <div className="text-[11px] tracking-[0.3em] uppercase text-brand-red mb-3">
                        {CATEGORY_LABELS[workout.category]} · Run mode
                    </div>
                    <h1 className="font-display text-4xl md:text-5xl tracking-wider leading-[0.95] text-white">
                        {workout.title}
                    </h1>
                    <div className="mt-3 text-xs text-zinc-200 flex items-center gap-4 flex-wrap">
                        <span className="inline-flex items-center gap-1.5"><Layers className="w-3.5 h-3.5 text-brand-red" /> {workout.blocks.length} block{workout.blocks.length === 1 ? "" : "s"}</span>
                        {workout.estimated_duration && <span className="inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-brand-red" /> {workout.estimated_duration} min</span>}
                    </div>
                </div>
            </section>

            <section className="py-8">
                <div className="max-w-4xl mx-auto px-6 md:px-10 space-y-6">
                    <BodyweightCard athleteId={athleteId} athleteName={athleteName} />
                    <div className="border border-zinc-900 bg-zinc-950 p-6 md:p-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div>
                                <div className="text-[10px] tracking-[0.25em] uppercase text-brand-red mb-1">
                                    Ready to lift
                                </div>
                                <div className="font-display text-2xl tracking-wider text-white">
                                    {phase === "done" ? "Workout complete." : focusOpen ? "Focus Mode is paused." : "Tap Start to enter Focus Mode."}
                                </div>
                                <p className="text-sm text-zinc-200 mt-1">
                                    Hand the tablet to {athlete?.first_name || "the athlete"}, or drive it yourself. Plates and rest are timed.
                                </p>
                                {missingMaxes.length > 0 && (
                                    <p className="text-[11px] tracking-wider text-amber-300 mt-2">
                                        Enter their max inline as you reach each lift — it saves when you tap Set Complete.
                                    </p>
                                )}
                            </div>
                            <button
                                type="button"
                                data-testid={focusOpen ? "resume-focus" : "start-focus"}
                                onClick={() => focusOpen ? setFocusOpen(true) : startFocus()}
                                disabled={phase === "done"}
                                className="btn-tactical inline-flex disabled:opacity-50"
                            >
                                <Play className="w-4 h-4" /> {focusOpen ? "Resume Focus Mode" : "Start workout"}
                            </button>
                        </div>

                    {/* Read-only map */}
                    <div className="space-y-4">
                        {workout.blocks.map((b, bi) => (
                            <div key={b.id || bi} className="border border-zinc-900 bg-zinc-950">
                                <div className="px-5 py-3 border-b border-zinc-900 flex items-center justify-between">
                                    <div className="flex items-center gap-3">
                                        <span className="font-display text-2xl tracking-wider text-brand-red">{b.name}</span>
                                        {bi === blockIdx && focusOpen && (
                                            <span className="text-[10px] tracking-[0.25em] uppercase border border-brand-red text-brand-red px-2 py-1 animate-pulse">In progress</span>
                                        )}
                                    </div>
                                </div>
                                {b.coach_note && (
                                    <div className="px-5 py-2 border-b border-zinc-900 bg-black/40 flex items-start gap-2 text-xs text-zinc-300">
                                        <MessageSquare className="w-3 h-3 text-brand-red flex-shrink-0 mt-0.5" /> {b.coach_note}
                                    </div>
                                )}
                                {b.levels.map((l, li) => (
                                    <div key={li} className="px-5 py-3 border-t border-zinc-900 first:border-t-0">
                                        <div className="flex items-center justify-between gap-3">
                                            <div>
                                                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">Level {l.level}</div>
                                                <div className="font-display text-xl tracking-wider text-white">{l.exercise_name}</div>
                                            </div>
                                            <div className="text-[10px] tracking-[0.2em] uppercase text-zinc-300">
                                                {l.sets_data.length} sets
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {focusOpen && (
                <FocusModeOverlay
                    workout={workout}
                    blockIdx={blockIdx}
                    setIdx={setIdx}
                    selectedLevels={selectedLevels}
                    actuals={actuals}
                    setActuals={setActuals}
                    phase={phase}
                    pendingDelta={pendingDelta}
                    setPendingDelta={setPendingDelta}
                    maxes={maxes}
                    equipmentMap={equipmentMap}
                    exerciseMap={exerciseMap}
                    onClose={() => setFocusOpen(false)}
                    onSetComplete={handleSetComplete}
                    onSkipRest={() => {}}
                    onConfirmWheel={handleConfirmWheel}
                    onSaveMax={saveMaxInline}
                    sessionStats={{ setsCompleted, elapsedSec }}
                />
            )}
        </div>
    );
}
